/**
 * Generic HTTP Service
 * Framework-agnostic HTTP client with configurable base URL and auth token
 */

import type { ApiError, HttpConfig } from '@/lib/ui/types';

export class GenericHttpService {
  private apiBase = '';
  private defaultHeaders: Record<string, string> = {};
  private authToken: string | null = null;
  private storage?: {
    get: <T>(key: string) => T | null;
    set: <T>(key: string, value: T) => void;
    remove: (key: string) => void;
  };

  init(config: HttpConfig & { storage?: GenericHttpService['storage'] }): void {
    this.apiBase = config.apiBase;
    this.defaultHeaders = config.defaultHeaders || {};
    this.storage = config.storage;
    
    // Initialize token from storage if available
    if (this.storage) {
      this.authToken = this.storage.get<string>('auth_token');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, string>; credentials?: RequestCredentials } = {},
    includeAuth: boolean = true
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${this.apiBase}${endpoint}`;
    if (params) url += '?' + new URLSearchParams(params).toString();

    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...this.defaultHeaders,
      ...(includeAuth && this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {}),
      ...(fetchOptions.headers as Record<string, string>),
    };

    const response = await fetch(url, { ...fetchOptions, headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw { status: response.status, data: errorData } as ApiError;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : ({} as T);
  }

  get = <T>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>) =>
    this.request<T>(endpoint, { method: 'GET', params, headers }, true);

  getWithCredentials = <T>(endpoint: string, headers?: Record<string, string>) =>
    this.request<T>(endpoint, { method: 'GET', credentials: 'include', headers }, true);

  post = <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    }, true);

  put = <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    }, true);

  patch = <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    }, true);

  delete = <T>(endpoint: string, headers?: Record<string, string>) =>
    this.request<T>(endpoint, { method: 'DELETE', headers }, true);

  deleteWithBody = <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    }, true);

  public = {
    get: <T>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>) =>
      this.request<T>(endpoint, {
        method: 'GET',
        params,
        headers,
        credentials: endpoint.startsWith('/auth/oauth/') ? 'include' : 'same-origin',
      }, false),
    post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
      this.request<T>(endpoint, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
        headers,
        credentials: endpoint.startsWith('/auth/oauth/') ? 'include' : 'same-origin',
      }, false),
  };

  postWithProgress = <T>(
    endpoint: string,
    data: FormData,
    onProgress?: (percent: number) => void,
    headers?: Record<string, string>
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${this.apiBase}${endpoint}`;

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve(xhr.responseText as T);
          }
        } else {
          let errorData;
          try {
            errorData = JSON.parse(xhr.responseText);
          } catch {
            errorData = { message: xhr.statusText };
          }
          reject({ status: xhr.status, data: errorData } as ApiError);
        }
      });

      xhr.addEventListener('error', () => {
        reject({ status: 0, data: { message: 'Network error' } } as ApiError);
      });

      xhr.addEventListener('abort', () => {
        reject({ status: 0, data: { message: 'Request aborted' } } as ApiError);
      });

      xhr.open('POST', url);
      xhr.setRequestHeader('Accept', 'application/json');

      if (this.authToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.authToken}`);
      }

      if (headers) {
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }

      xhr.send(data);
    });
  };

  setAuthToken(token: string | null): void {
    this.authToken = token;
    if (this.storage) {
      if (token) this.storage.set('auth_token', token);
      else this.storage.remove('auth_token');
    }
  }

  getAuthToken(): string | null {
    return this.authToken;
  }
}

// Export singleton instance
export const httpService = new GenericHttpService();
